import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'hooks/useDebounceCallback';
import SearchInputView from 'components/SearchInputView';

interface DebouncedSearchInputProps {
  value: string;
  onSearch: (value: string) => void;
  debounceDelay?: number;
  className?: string;
  placeholder?: string;
  id?: string;
}

const DebouncedSearchInput = ({
  value,
  onSearch,
  debounceDelay = 400,
  className,
  placeholder,
  id,
}: DebouncedSearchInputProps) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Keep a ref so the debounced callback below can stay referentially
  // stable across renders even if the caller passes a fresh `onSearch`
  // each time - otherwise useDebounceCallback recreates its timer on every
  // keystroke instead of batching them.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const stableOnSearch = useCallback(
    (newValue: string) => onSearchRef.current(newValue),
    [],
  );

  const onSearchDebounced = useDebounceCallback(stableOnSearch, debounceDelay);

  return (
    <SearchInputView
      id={id}
      className={className}
      placeholder={placeholder}
      value={inputValue}
      onChange={(newValue) => {
        setInputValue(newValue);
        onSearchDebounced(newValue);
      }}
      onClear={() => {
        setInputValue('');
        onSearchRef.current('');
      }}
    />
  );
};

export default DebouncedSearchInput;
